import { RequestOpenAxios } from 'src/features/access/APIUtils'

// Define response type for cache
interface CachedResponse<T> {
  data: T;
  timestamp: number;
}

// Cache to store in-flight requests and their promises
const pendingRequests: Record<string, Promise<unknown>> = {};

// Cache for completed request results to prevent duplicate fetches even after completion
// This is particularly important for news lists that don't change often
const completedRequestCache: Record<string, CachedResponse<unknown>> = {};

// How long to cache completed requests (milliseconds)
const CACHE_DURATION = 30000; // 30 seconds

// Function to generate a unique key for each request based on parameters
const generateRequestKey = (method: string, url: string, params?: Record<string, unknown>): string => {
  let key = `${method}:${url}`;

  if (params) {
    if (url.includes('/xnews/list') && params.lang) {
      key += `:lang=${params.lang}`;

      // Always include type in the key, defaulting to null if not present
      key += `:type=${params.type ?? null}`;

      if (params.subtype) {
        key += `:subtype=${params.subtype}`;
      }

      if (params.startTime) {
        key += `:startTime=${params.startTime}`;
      }

      if (params.endTime) {
        key += `:endTime=${params.endTime}`;
      }

      if (params.minTrendingScore !== undefined) {
        key += `:minTrendingScore=${params.minTrendingScore}`;
      }

      if (params.forceRefresh) {
        key += `:forceRefresh=${params.forceRefresh}`;
      }

      key += `:page=${params.page || 0}`;
    } else {
      key += `:${JSON.stringify(params)}`;
    }
  }

  return key;
};

// Create a deduplication wrapper function for API calls
const deduplicateRequest = async <T,>(
  method: string,
  url: string,
  params?: Record<string, unknown>,
  responseType = 'json'
): Promise<T> => {
  const requestKey = generateRequestKey(method, url, params);

  // Skip cache for requests with forceRefresh
  if (!params?.forceRefresh) {
    const cachedResult = completedRequestCache[requestKey];
    if (cachedResult && Date.now() - cachedResult.timestamp < CACHE_DURATION) {
      console.log(`Using cached result for ${requestKey}`);
      return cachedResult.data as T;
    }
  }

  if (requestKey in pendingRequests) {
    console.log(`Using existing in-flight request for ${requestKey}`);
    return pendingRequests[requestKey] as Promise<T>;
  }

  console.log(`Making new request for ${requestKey}`);

  // Ensure we preserve null values and minTrendingScore in the request parameters
  const requestParams = params ? { ...params } : undefined;
  if (requestParams?.forceRefresh) {
    delete requestParams.forceRefresh;
  }

  // Log all parameters being sent to backend
  console.log('Request parameters:', requestParams);

  const requestPromise = RequestOpenAxios({
    method,
    url,
    params: requestParams,
    responseType
  }).then(response => {
    delete pendingRequests[requestKey];

    const result = response.status === 200 ? response.data : (method === 'GET' ? [] : null);

    if (method === 'GET' && result && !params?.forceRefresh) {
      completedRequestCache[requestKey] = {
        data: result,
        timestamp: Date.now()
      };
    }

    return result;
  }).catch(error => {
    delete pendingRequests[requestKey];
    console.error(`Error in API request ${requestKey}:`, error);
    return method === 'GET' ? [] : null;
  });

  pendingRequests[requestKey] = requestPromise;

  return requestPromise as Promise<T>;
};

export interface NewsArticle {
  id: string;
  image_url: string;
  string: number;
  news: string;
  source: string;
  title: string;
  type: string;
  subtype: string;
  avail_langs: string[];
  hits: number;
  img_num: number;
  video_num: number;
  postTime: number;
  cita: string;
  img_urls: string[];
  video_embed_link: string;
}

export interface NewsListItems {
  id: string;
  source: string;
  title: string;
  type: string;
  subtype: string;
  hits: number;
  img_num: number;
  video_num: number;
  postTime: number;
  img_urls: string[];
  video_embed_link: string;
}



export function sortNewsArticles(articles: NewsListItems[]): NewsListItems[] {
  if (!articles) return [];

  const HITS_PER_MINUTE_THRESHOLD = 500;

  // const now = Date.now();

  // Precompute rates for each article
  const articlesWithScores = articles.map((articleA) => {
    let score = 0;

    for (const articleB of articles) {
      if (articleA.id === articleB.id) continue;

      const deltaHits = Math.abs(articleB.hits - articleA.hits);
      const deltaMinutes = Math.max(
        Math.abs(articleB.postTime - articleA.postTime) / (1000 * 60),
        1
      );

      const rate = deltaHits / deltaMinutes;

      if (rate > HITS_PER_MINUTE_THRESHOLD) {
        score++;
      }
    }

    return { ...articleA, velocityScore: score };
  });

  return articlesWithScores.sort((a, b) => {
    if (b.velocityScore !== a.velocityScore) {
      return b.velocityScore - a.velocityScore;
    }

    if (b.hits !== a.hits) {
      return b.hits - a.hits;
    }

    return b.postTime - a.postTime;
  });
}





export const fetchLatestNews = async (count = 10): Promise<NewsArticle[]> => {
  try {
    return await deduplicateRequest<NewsArticle[]>('GET', `/api/public/xnews/latest`, { count });
  } catch (error) {
    console.error("Error fetching latest news:", error);
    return []; // Return an empty array in case of error
  }
};

// API function to fetch a list of news articles
export const fetchNewsList = async (
  params: {
    startTime: number | undefined;
    endTime: number | undefined;
    page: number;
    size: number;
    lang: string;
    daysBack: number | undefined;
    type?: string | null;  // Make type optional
    subtype?: string;
    minTrendingScore?: number; // Add minimum trending score parameter
    forceRefresh?: boolean;
  }
): Promise<NewsListItems[]> => {
  console.log('[NewsAPIUtil] fetchNewsList called with params:', params);

  // Construct API parameters
  const apiParams: Record<string, unknown> = {
    lang: params.lang || 'en',
    page: params.page,
    size: params.size,
    daysBack: params.daysBack,
    forceRefresh: params.forceRefresh
  };

  // Only add parameters that are defined
  if (params.type !== undefined) {
    apiParams.type = params.type;
  }

  if (params.subtype !== undefined) {
    apiParams.subtype = params.subtype;
  }

  if (params.startTime !== undefined) {
    apiParams.startTime = params.startTime;
  }

  if (params.endTime !== undefined) {
    apiParams.endTime = params.endTime;
  }

  if (params.minTrendingScore !== undefined) {
    apiParams.minTrendingScore = params.minTrendingScore;
  }

  console.log('[NewsAPIUtil] Making request with params:', apiParams);

  try {
    const response = await deduplicateRequest<NewsListItems[]>(
      'GET', 
      '/api/public/xnews/list', 
      apiParams
    );

    // Log the result
    const result = { 
      resultLength: response?.length,
      requestParams: apiParams
    };
    console.log('[NewsAPIUtil] deduplicateRequest returned:', result);

    return response;
  } catch (error) {
    console.error('[NewsAPIUtil] Error in fetchNewsList:', error);
    return [];
  }
};

export const fetchNewsByDateRange = async ({
  startTime,
  endTime,
  page = 0,
  size = 1000,
  lang,
  type,
  subtype,
  minTrendingScore,
  forceRefresh
}: {
  startTime: number | string,
  endTime: number | string,
  page?: number,
  size?: number,
  lang?: string,
  type?: string | null,  // Make type optional
  subtype?: string,
  minTrendingScore?: number, // Add minimum trending score parameter
  forceRefresh?: boolean
}): Promise<NewsListItems[]> => {
  // Convert ISO string dates to timestamps if needed
  const start = typeof startTime === 'string' ? new Date(startTime).getTime() : startTime;
  const end = typeof endTime === 'string' ? new Date(endTime).getTime() : endTime;

  console.log(`[NewsAPIUtil] fetchNewsByDateRange called with params - lang: ${lang}, type: ${type}, subtype: ${subtype}, minTrendingScore: ${minTrendingScore}`);

  // Create params object with all required parameters
  const params = {
    startTime: start,
    endTime: end,
    lang: lang || 'en',
    daysBack: undefined,
    page,
    size,
    type,  // Pass type as is, it will be handled by fetchNewsList
    subtype,
    minTrendingScore, // Pass through to fetchNewsList
    forceRefresh
  };

  console.log(`[NewsAPIUtil] Calling fetchNewsList with params:`, params);

  try {
    const news = await fetchNewsList(params);
    console.log(`[NewsAPIUtil] fetchNewsList returned ${news.length} items`);
    return news;
  } catch (error) {
    console.error(`[NewsAPIUtil] Error in fetchNewsByDateRange:`, error);
    throw error;
  }
};

export const fetchArchiveNewsByDate = async ({
  date,
  type,
  subtype,
  minTrendingScore,
  forceRefresh
}: {
  date: string;
  type?: string | null;  // Make type optional
  subtype?: string;
  minTrendingScore?: number; // Add minimum trending score parameter
  forceRefresh?: boolean;
}): Promise<NewsListItems[]> => {
  console.log(`[NewsAPIUtil] Fetching archive news for type: ${type}, subtype: ${subtype}, date: ${date}, minTrendingScore: ${minTrendingScore}`);

  try {
    // Convert date string (YYYYMMDD) to timestamp range
    const startDate = new Date(
      parseInt(date.substring(0, 4)),
      parseInt(date.substring(4, 6)) - 1,
      parseInt(date.substring(6, 8))
    );
    const endDate = new Date(startDate);
    endDate.setDate(endDate.getDate() + 1);

    const response = await fetchNewsList({
      startTime: startDate.getTime(),
      endTime: endDate.getTime(),
      page: 0,
      size: 100,
      lang: 'en',
      daysBack: undefined,
      type,  // Pass type as is, it will be handled by fetchNewsList
      subtype,
      minTrendingScore, // Pass through to fetchNewsList
      forceRefresh
    });

    console.log(`[NewsAPIUtil] Loaded ${response.length} news items with type: ${type}, subtype: ${subtype}`);
    return response;
  } catch (error) {
    console.error('[NewsAPIUtil] Error fetching archive news:', error);
    throw error;
  }
};

export const fetchNewsByDaysBack = async (
  daysBack: number,
  page: number,
  size: number,
  lang: string,
  type?: string | null,  // Make type optional
  subtype?: string,
  minTrendingScore?: number // Add minimum trending score parameter
): Promise<NewsListItems[]> => {
  const news = await fetchNewsList({
    startTime: undefined,
    endTime: undefined,
    page,
    size,
    lang,
    daysBack,
    type,
    subtype,
    minTrendingScore // Pass through to fetchNewsList
  });
  return news;
};

export const fetchAllNews = async (
  page: number,
  size: number,
  lang: string,
  type?: string | null,  // Make type optional
  subtype?: string,
  minTrendingScore?: number // Add minimum trending score parameter
): Promise<NewsListItems[]> => {
  const news = await fetchNewsList({
    startTime: undefined,
    endTime: undefined,
    page,
    size,
    lang,
    daysBack: undefined,
    type,
    subtype,
    minTrendingScore // Pass through to fetchNewsList
  });
  return news;
};

// API function to fetch a single news article by URL ID
export const fetchNewsById = async (id: string, lang: string): Promise<NewsArticle | null> => {
  return deduplicateRequest<NewsArticle | null>('GET', `/api/public/xnews/details/${id}`, { lang });
};


export const fetchNewsArchiveByMonth = async ({
  lang,
  category,
  yyyymm,
  page = 0,
  size = 100,
  forceRefresh = false
}: {
  lang: string;
  category: string;
  yyyymm: string;
  page?: number;
  size?: number;
  forceRefresh?: boolean;
}): Promise<NewsListItems[]> => {
  const url = `/api/public/xnews/archive/${lang}/${category}/${yyyymm}`;
  const params = { page, size, forceRefresh };

  try {
    const response = await deduplicateRequest<NewsListItems[]>('GET', url, params);
    console.log(`[NewsAPIUtil] fetchNewsArchiveByMonth returned ${response.length} items`);
    return response;
  } catch (error) {
    console.error(`[NewsAPIUtil] Error in fetchNewsArchiveByMonth:`, error);
    return [];
  }
};




/**
 * Fetch relevant news by subtype and postTime window using backend /xnews/list API.
 * @param lang Language code, e.g. 'en'
 * @param subtype Subtype string, e.g. 'politics'
 * @param postTime Current news postTime in milliseconds (UNIX epoch)
 * @param windowMs Time window (ms) before/after postTime, default 3 days
 * @param maxResults Max number of news items to return, default 5
 * @returns Promise resolving to array of NewsListItems
 */
export const fetchRelevantNewsBySubtypeAndPostTime = async (
  lang: string,
  subtype: string,
  postTime: number,
  windowMs = 1000 * 60 * 60 * 24 * 120, // 120 days
  maxResults = 10
): Promise<NewsListItems[]> => {
  try {

    console.log('[NewsAPIUtil] fetchRelevantNewsBySubtypeAndPostTime called with:', lang, subtype, postTime, windowMs, maxResults);
    const startTime = postTime*1000 - windowMs;
    const endTime = postTime*1000 + windowMs;

    const params = {
      lang,
      subtype,
      startTime,
      endTime,
      page: 0,
      size: maxResults,
    };

    console.log('[NewsAPIUtil] fetchRelevantNewsBySubtypeAndPostTime called with:', params);

    const response = await deduplicateRequest<NewsListItems[]>('GET', '/api/public/xnews/list', params);

    console.log(`[NewsAPIUtil] fetchRelevantNewsBySubtypeAndPostTime returned ${response.length} items`);

    return response;
  } catch (error) {
    console.error('[NewsAPIUtil] Error in fetchRelevantNewsBySubtypeAndPostTime:', error);
    return [];
  }
};
